import * as request from "src/utils/request";
import * as auth from "src/utils/auth";
import {
  EmailSearchParams,
  EmailConversationSearchResponse,
  EmailMessageSearchResponse,
  MailboxOption,
  FullTextSearchConversation,
} from "../types/email";
import getConfig from "next/config";
import { AnalyticsAction, AnalyticsModule } from "src/config/analytics/taxonomy";
import { track, trackTimed } from "src/config/analytics/track";

const { publicRuntimeConfig } = getConfig();

type EmailSSEMessageHandler = (data: unknown) => void;
type EmailSSEErrorHandler = (error: Error) => void;
type EmailSSEConnection = { close: () => void };

export const emailApi = {
  searchConversations: (params: EmailSearchParams): Promise<EmailConversationSearchResponse> => {
    const searchParams = new URLSearchParams();

    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());

    if (params.search) {
      searchParams.append("search", params.search);
    }

    // Append any other properties in params that aren't handled explicitly
    Object.keys(params).forEach((key) => {
      if (
        ![
          "page",
          "size",
          "sort",
          "status",
          "search",
          "assignee",
          "category",
          "bookingStatus",
        ].includes(key) &&
        params[key] !== undefined
      ) {
        searchParams.append(key, params[key].toString());
      }
    });

    let queryString = searchParams.toString();

    // Handle OData filters
    const filters: string[] = [];
    if (params.status) {
      filters.push(`taskStatus eq '${params.status}'`);
    }
    if (params.assignee) {
      filters.push(`assignee eq '${params.assignee}'`);
    }
    if (params.bookingStatus) {
      filters.push(`bookingStatus eq '${params.bookingStatus}'`);
    }
    if (params.mailbox) {
      filters.push(`mailbox eq '${params.mailbox}'`);
    }
    if (params.category) {
      const categories = params.category
        .split(",")
        .map((c: string) => c.trim())
        .filter(Boolean);
      if (categories.length === 1) {
        filters.push(`messages.categories eq '${categories[0]}'`);
      } else if (categories.length > 1) {
        const values = categories.map((c: string) => `'${c}'`).join(",");
        filters.push(`messages.categories in [${values}]`);
      }
    }
    if (filters.length > 0) {
      const filterString = filters.join(" and ");
      queryString += `${queryString ? "&" : ""}$filter=${encodeURIComponent(filterString)}`;
    }

    if (params.sort) {
      queryString += `${queryString ? "&" : ""}$orderby=${encodeURIComponent(params.sort)}`;
    } else {
      queryString += `${queryString ? "&" : ""}$orderby=${encodeURIComponent(
        "messages.sentDateTime desc"
      )}`;
    }

    const finalUrl = `invoicing/api/internal/emailConversations/search?${queryString}`;
    console.log("Search Email Conversations URL:", finalUrl);

    request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
    return request.get(finalUrl);
  },

  searchMessages: (
    params: EmailSearchParams,
    signal?: AbortSignal
  ): Promise<EmailMessageSearchResponse> => {
    const searchParams = new URLSearchParams();

    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());

    if (params.search) {
      searchParams.append("search", params.search);
    }

    const filters: string[] = [];

    // Append any other properties in params that aren't handled explicitly
    Object.keys(params).forEach((key) => {
      if (
        !["page", "size", "sort", "search", "conversationId", "mailbox", "messageIds"].includes(
          key
        ) &&
        params[key] !== undefined
      ) {
        // This is fallback for other params, assuming they are not part of filter for now
        // OR we could treat them as simple params
        searchParams.append(key, params[key].toString());
      }
    });

    if (params.conversationId) {
      filters.push(`msConversationId eq '${params.conversationId}'`);
    }
    if (params.mailbox) {
      filters.push(`mailbox eq '${params.mailbox}'`);
    }

    if (params.messageIds) {
      const ids = [...new Set(params.messageIds.map((id) => id.trim()).filter(Boolean))];
      if (ids.length === 0) {
        return Promise.reject(new Error("At least one message ID is required"));
      }
      filters.push(`id in [${ids.map((id) => `'${id.replace(/'/g, "''")}'`).join(",")}]`);
    }

    let queryString = searchParams.toString();
    if (filters.length > 0) {
      const filterString = filters.join(" and ");
      queryString += `${queryString ? "&" : ""}$filter=${encodeURIComponent(filterString)}`;
    }

    if (params.sort) {
      queryString += `${queryString ? "&" : ""}$orderby=${encodeURIComponent(params.sort)}`;
    } else {
      queryString += `${queryString ? "&" : ""}$orderby=${encodeURIComponent("sentDateTime desc")}`;
    }
    request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
    const url = `invoicing/api/internal/emailMessages/search?${queryString}`;
    return signal ? request.getWithSignal(url, signal) : request.get(url);
  },

  connectNewMailSSE: async (
    onMessage: EmailSSEMessageHandler,
    onError?: EmailSSEErrorHandler
  ): Promise<EmailSSEConnection> => {
    const sseUrl = `${publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL}/oneplatform/api/internal/sse/connect`;
    const token = auth.getToken();
    const abortController = new AbortController();
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let keepaliveTimer: ReturnType<typeof setTimeout> | null = null;
    let fetchTimeout: ReturnType<typeof setTimeout> | null = null;
    let manuallyClosed = false;
    let fetchTimeoutError: Error | null = null;

    const clearTimers = () => {
      if (keepaliveTimer) {
        clearTimeout(keepaliveTimer);
        keepaliveTimer = null;
      }
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
        fetchTimeout = null;
      }
    };

    const close = () => {
      manuallyClosed = true;
      clearTimers();
      abortController.abort();
      reader?.cancel().catch(() => {
        // Ignore errors when canceling an already closed stream.
      });
    };

    const failConnection = (error: Error) => {
      if (!manuallyClosed) {
        onError?.(error);
      }
      close();
    };

    const resetKeepaliveTimer = () => {
      if (keepaliveTimer) clearTimeout(keepaliveTimer);
      keepaliveTimer = setTimeout(() => {
        failConnection(new Error("Email SSE keepalive timeout"));
      }, 60000);
    };

    try {
      fetchTimeout = setTimeout(() => {
        fetchTimeoutError = new Error("Email SSE fetch timeout");
        abortController.abort();
      }, 60000);

      const response = await fetch(sseUrl, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache",
        },
        signal: abortController.signal,
      });

      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
        fetchTimeout = null;
      }

      if (response.status === 401) {
        if (location.pathname !== "/login") {
          auth.clearToken();
        }
        throw new Error("Unauthorized: 401");
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let eventName = "";

      const processStream = async () => {
        try {
          resetKeepaliveTimer();

          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader!.read();

            if (done) {
              if (!manuallyClosed) {
                failConnection(new Error("Email SSE stream ended"));
              }
              break;
            }

            if (!value) continue;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              const normalizedLine = line.replace(/\r$/, "");
              if (normalizedLine.trim() === "") {
                eventName = "";
                continue;
              }
              if (normalizedLine.startsWith("event:")) {
                eventName = normalizedLine.slice(6).trim().toLowerCase();
                continue;
              }
              if (!normalizedLine.startsWith("data:")) continue;

              const rawData = normalizedLine.slice(5);
              const data = rawData.startsWith(" ") ? rawData.slice(1) : rawData;
              const normalizedData = data.trim().toLowerCase();

              if (
                eventName === "heartbeat" ||
                normalizedData === "keepalive" ||
                normalizedData === "heartbeat"
              ) {
                resetKeepaliveTimer();
                eventName = "";
                continue;
              }

              if (data === "[DONE]") {
                close();
                return;
              }

              try {
                const parsed = JSON.parse(data);
                const parsedRecord =
                  typeof parsed === "object" && parsed !== null
                    ? (parsed as Record<string, unknown>)
                    : null;
                const parsedEvent = String(
                  parsedRecord?.type || parsedRecord?.event || parsedRecord?.event_type || ""
                )
                  .trim()
                  .toLowerCase();
                if (parsedEvent === "heartbeat" || parsedEvent === "keepalive") {
                  resetKeepaliveTimer();
                  eventName = "";
                  continue;
                }
                onMessage(parsed);
              } catch {
                onMessage(data);
              }
              eventName = "";
            }
          }
        } catch (streamError) {
          if (streamError instanceof Error && streamError.name === "AbortError") {
            return;
          }
          failConnection(streamError as Error);
        }
      };

      void processStream();

      return { close };
    } catch (error) {
      clearTimers();
      if (fetchTimeoutError && error instanceof Error && error.name === "AbortError") {
        throw fetchTimeoutError;
      }
      throw error;
    }
  },
};

interface Attachment {
  attachmentId: number | string;
  name: string;
  type: string;
}

interface ReplyAllEmailPayload {
  comment: string;
  subject?: string;
  attachments?: Attachment[];
}

interface ReplyEmailPayload extends ReplyAllEmailPayload {
  toRecipients: string[];
  ccRecipients: string[];
  bccRecipients?: string[];
}

/**
 * Reply to all recipients of a message
 * @param messageId - The internal DB ID of the message (Long type), not the Microsoft ID
 * @param data - The reply content and attachments
 */
export async function replyAll(messageId: string, data: ReplyAllEmailPayload) {
  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  return trackTimed(
    AnalyticsModule.Station,
    AnalyticsAction.RecordSubmit,
    () => request.post(`invoicing/api/internal/email/message/${messageId}/replyAll`, data),
    { target: "reply_all" }
  );
}

/**
 * Reply to selected recipients of a message
 * @param messageId - The internal DB ID of the message (Long type), not the Microsoft ID
 * @param data - The reply content, recipients, and attachments
 */
export async function reply(messageId: string, data: ReplyEmailPayload) {
  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  return trackTimed(
    AnalyticsModule.Station,
    AnalyticsAction.RecordSubmit,
    () => request.post(`invoicing/api/internal/email/message/${messageId}/reply`, data),
    { target: "reply" }
  );
}

export async function assignConversationToMe(conversationId: string) {
  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  return trackTimed(
    AnalyticsModule.Station,
    AnalyticsAction.RecordUpdate,
    () => request.post(`invoicing/api/internal/email/conversation/${conversationId}/assignToMe`),
    { target: "assign_to_me" }
  );
}

export async function releaseConversation(conversationId: string) {
  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  return trackTimed(
    AnalyticsModule.Station,
    AnalyticsAction.RecordUpdate,
    () => request.post(`invoicing/api/internal/email/conversation/${conversationId}/release`),
    { target: "release" }
  );
}

export async function closeConversation(conversationId: string) {
  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  return trackTimed(
    AnalyticsModule.Station,
    AnalyticsAction.RecordApprove,
    () => request.post(`invoicing/api/internal/email/conversation/${conversationId}/close`),
    { target: "close_conversation" }
  );
}

export interface EmailTemplateOption {
  templateId: string;
  templateName: string;
}

export interface BookingTemplateOrderOption {
  orderId: string;
  hawbNo?: string;
}

export interface EmailTemplateHtmlResponse {
  templateHtml: string;
  orderSelectionRequired: boolean;
  orderOptions: BookingTemplateOrderOption[];
  selectedOrderId?: string;
  unresolvedTokens: string[];
}

export interface EmailTemplateListResponse {
  templates: EmailTemplateOption[];
  orderOptions: BookingTemplateOrderOption[];
}

const normalizeTemplateOptions = (response: any): EmailTemplateOption[] => {
  // Response shape: { conversation_id, templates: [{ _id, template_name }] }
  const source: any[] = Array.isArray(response?.templates) ? response.templates : [];

  return source
    .map((item: any) => {
      const rawId = item?._id;
      if (rawId === undefined || rawId === null) return null;
      return {
        templateId: String(rawId),
        templateName: item?.template_name || String(rawId),
      };
    })
    .filter(Boolean) as EmailTemplateOption[];
};

const normalizeTemplateOrderOptions = (orders: unknown): BookingTemplateOrderOption[] =>
  Array.isArray(orders)
    ? orders
        .filter(
          (item: unknown): item is { order_id?: unknown; hawb_no?: unknown } =>
            typeof item === "object" && item !== null
        )
        .map((item) => ({
          orderId: typeof item.order_id === "string" ? item.order_id.trim() : "",
          hawbNo: typeof item.hawb_no === "string" ? item.hawb_no : undefined,
        }))
        .filter((item) => Boolean(item.orderId))
    : [];

export async function getEmailTemplates(
  conversationId: string,
  mailbox: string,
  messageId?: string
): Promise<EmailTemplateListResponse> {
  const normalizedMailbox = mailbox.trim();
  if (!normalizedMailbox) {
    throw new Error("A mailbox is required to load email templates.");
  }
  const normalizedMessageId = messageId?.trim();

  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  let url = `oneplatform-agent/template?conversation_id=${encodeURIComponent(
    conversationId
  )}&mailbox=${encodeURIComponent(normalizedMailbox)}`;
  if (normalizedMessageId) {
    url += `&message_id=${encodeURIComponent(normalizedMessageId)}`;
  }
  const response = await request.get(url);

  return {
    templates: normalizeTemplateOptions(response),
    orderOptions: normalizeTemplateOrderOptions(response?.orders),
  };
}

export async function getMailboxList(): Promise<MailboxOption[]> {
  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  const response = await request.get("invoicing/api/internal/mailboxes/list");
  //   return [];
  return Array.isArray(response) ? response : [];
}

export async function getEmailTemplateHtml(
  conversationId: string,
  templateId: string,
  mailbox: string,
  messageId: string,
  orderId?: string
): Promise<EmailTemplateHtmlResponse> {
  const normalizedMailbox = mailbox.trim();
  if (!normalizedMailbox) {
    throw new Error("A mailbox is required to load email template content.");
  }
  const normalizedMessageId = messageId.trim();
  if (!normalizedMessageId) {
    throw new Error("A message is required to load email template content.");
  }
  const normalizedOrderId = orderId?.trim();

  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  const response = await request.post("oneplatform-agent/template", {
    conversation_id: conversationId,
    mailbox: normalizedMailbox,
    template_id: templateId,
    message_id: normalizedMessageId,
    order_id: normalizedOrderId || null,
  });

  if (typeof response === "string") {
    return {
      templateHtml: response,
      orderSelectionRequired: false,
      orderOptions: [],
      unresolvedTokens: [],
    };
  }

  if (response?.order_selection_required) {
    return {
      templateHtml: "",
      orderSelectionRequired: true,
      orderOptions: normalizeTemplateOrderOptions(response.orders),
      unresolvedTokens: [],
    };
  }

  return {
    templateHtml: response?.templates?.[0]?.template || response?.template || "",
    orderSelectionRequired: false,
    orderOptions: normalizeTemplateOrderOptions(response?.orders),
    selectedOrderId:
      typeof response?.order_id === "string" && response.order_id.trim()
        ? response.order_id.trim()
        : undefined,
    unresolvedTokens: Array.isArray(response?.unresolved_tokens)
      ? response.unresolved_tokens.filter(
          (token: unknown): token is string => typeof token === "string"
        )
      : [],
  };
}

export async function fullTextSearchConversations(params: {
  searchTerm: string;
  mailbox?: string;
  minScore?: number;
  maxScore?: number;
  page?: number;
  size?: number;
}): Promise<FullTextSearchConversation[]> {
  const searchParams = new URLSearchParams();
  searchParams.append("searchTerm", params.searchTerm);
  if (params.mailbox) {
    searchParams.append("mailbox", params.mailbox);
  }
  if (params.minScore !== undefined) {
    searchParams.append("minScore", params.minScore.toString());
  }
  if (params.maxScore !== undefined) {
    searchParams.append("maxScore", params.maxScore.toString());
  }
  if (params.page !== undefined) {
    searchParams.append("page", params.page.toString());
  }
  if (params.size !== undefined) {
    searchParams.append("size", params.size.toString());
  }

  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  const response = await trackTimed(
    AnalyticsModule.Station,
    AnalyticsAction.SearchApply,
    () =>
      request.get(
        `invoicing/api/internal/email/conversation/fullTextSearch?${searchParams.toString()}`
      ),
    { target: "full_text_search" }
  );
  const rows = Array.isArray(response) ? response : [];
  // A search that returns nothing is a usability signal, not a failure — recorded separately so
  // "people search and find nothing" is answerable without inferring it from a gap in the funnel.
  if (rows.length === 0) {
    track(AnalyticsModule.Station, AnalyticsAction.StateEmpty, { target: "full_text_search" });
  }
  return rows;
}

export interface BookingOrderIdResponse {
  conversation_id: string;
  order_id?: string | null;
  order_ids?: Array<
    | string
    | null
    | {
        message_id: string | number;
        order_id: Array<string | null>;
      }
  >;
  message_id?: string | number;
  created_at?: string;
}

export interface TriggerNewBookingRequest {
  mailbox: string;
  conversation_id: string;
  message_id: string;
  operator_id: string;
}

export interface TriggerNewBookingEvent {
  event_type: string;
  order_id: string | null;
  error_code?: string | null;
  error_message?: string | null;
  error_type?: string | null;
}

export interface TriggerNewBookingResponse {
  conversation_id?: string;
  message_id?: string;
  datetime?: string;
  events?: TriggerNewBookingEvent[];
  orderID?: string;
  order_id?: string;
}

export interface TriggerNotBookingRequest {
  conversation_id: string;
  message_id: string;
  operator_id: string;
}

export interface TriggerNotBookingResponse {
  status: string;
  message: string;
  conversation_id: string;
  message_id: string;
  unlinked_order_ids: string[];
  message_status: null;
}

export interface TriggerEmailProcessingRequest {
  mailbox: string;
  conversation_id: string;
  message_id: string;
  intent: string;
  user_name: string;
}

/**
 * Resolve the booking order id for a conversation.
 *
 * The agent creates the booking order asynchronously, so this endpoint returns
 * 404 (rejects) while the order does not exist yet. Callers poll until an
 * booking order mappings are returned.
 *
 * @param conversationId - The conversation list id (e.g. "417738835972235264")
 */
export async function getBookingOrderId(conversationId: string): Promise<BookingOrderIdResponse> {
  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  return request.get(`oneplatform-agent/booking/order-id/${encodeURIComponent(conversationId)}`);
}

export async function triggerNewBooking(
  payload: TriggerNewBookingRequest
): Promise<TriggerNewBookingResponse> {
  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  return request.post("/booking/trigger-new-booking", payload);
}

export async function triggerNotBooking(
  payload: TriggerNotBookingRequest
): Promise<TriggerNotBookingResponse> {
  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  return request.post("/booking/trigger-not-booking", payload);
}

export async function triggerEmailProcessing(
  payload: TriggerEmailProcessingRequest
): Promise<unknown> {
  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  return request.post("/oneplatform/api/internal/email/trigger-processing", payload);
}

export async function searchByQuotationRef(params: {
  mailbox: string;
  quotationRef: string;
  page?: number;
  size?: number;
}): Promise<FullTextSearchConversation[]> {
  const searchParams = new URLSearchParams();
  searchParams.append("mailbox", params.mailbox);
  searchParams.append("quotationRef", params.quotationRef);
  // A partial quotation ref (e.g. just the mailbox prefix) can match many
  // quotations, so the same endpoint is paged via page/size.
  if (params.page !== undefined) {
    searchParams.append("page", params.page.toString());
  }
  if (params.size !== undefined) {
    searchParams.append("size", params.size.toString());
  }

  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  const response = await request.get(
    `invoicing/api/internal/email/conversation/searchByQuotationRef?${searchParams.toString()}`
  );
  return Array.isArray(response) ? response : [];
}

export async function searchByBookingRef(params: {
  mailbox: string;
  bookingRef: string;
}): Promise<FullTextSearchConversation[]> {
  const searchParams = new URLSearchParams();
  searchParams.append("mailbox", params.mailbox);
  searchParams.append("bookingRef", params.bookingRef);

  request.changeBaseUrl(publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL);
  const response = await request.get(
    `invoicing/api/internal/email/conversation/searchByBookingRef?${searchParams.toString()}`
  );
  return Array.isArray(response) ? response : [];
}
